// Button + modal to set product.metafields.inventory.limited_stock for storefront urgency messaging.
import React, { useState, useEffect } from 'react'
import moment from 'moment'
import { meta } from '../../../fnc'

const META_NAMESPACE = "inventory"
const META_KEY = "limited_stock"
const DEFAULT_QTY = 8

/**
	Instead of tracking sold units in a metafield, use **inventory delta**:

```
remaining = display_qty - (baseline_inventory - current_total_inventory)
```

As Shopify inventory drops from sales, the displayed number goes down automatically. Liquid can read `variant.inventory_quantity` on the storefront — no order webhooks or backend jobs.

**Liquid example for the theme:**

```liquid
{% assign ls = product.metafields.inventory.limited_stock.value %}
{% if ls.enabled %}
  {% assign total_inventory = 0 %}
  {% for variant in product.variants %}
    {% assign total_inventory = total_inventory | plus: variant.inventory_quantity %}
  {% endfor %}

  {% assign sold_since = ls.baseline_inventory | minus: total_inventory %}
  {% assign remaining = ls.display_qty | minus: sold_since %}

  {% if remaining > 0 %}
    <p class="limited-stock-urgency">Only {{ remaining }} left in stock - order soon</p>
  {% endif %}
{% endif %}
```
 */

// Sum inventory across all variants; used as the baseline snapshot when saving limited stock.
const getTotalProductInventory = (product) => {
	if(!product?.variants?.length)	return 0

	return product.variants.reduce((sum, variant) => {
		return sum + (parseInt(variant.inventory_quantity, 10) || 0)
	}, 0)
}

// Shopify may return metafield values as a JSON string or already-parsed object.
const parseLimitedStockMeta = (rawValue) => {
	if(!rawValue)	return null

	try {
		return typeof rawValue === "string"? JSON.parse(rawValue) : rawValue
	} catch (e) {
		return null
	}
}

// Estimate how many units the storefront should still display based on inventory sold since baseline.
const getLimitedStockRemaining = (metaValue, product) => {
	let data = parseLimitedStockMeta(metaValue)
	if(!data?.enabled)	return null

	let displayQty = parseInt(data.display_qty, 10)
	let baselineInventory = parseInt(data.baseline_inventory, 10)
	if(!displayQty || isNaN(baselineInventory))	return null

	let soldSince = baselineInventory - getTotalProductInventory(product)
	let remaining = displayQty - soldSince

	return remaining > 0? remaining : 0
}

// Locate the limited_stock entry inside a product metafields array.
const findLimitedStockMetafield = (metafields) => {
	return (metafields || []).find(o => o.namespace === META_NAMESPACE && o.key === META_KEY)
}

// Normalize a Shopify metafield record into local UI state (id, qty, active flag, parsed payload).
const readLimitedStockFromMetafield = (found) => {
	if(!found)	return { metaId: null, qty: DEFAULT_QTY, isActive: false, data: null }

	let data = parseLimitedStockMeta(found.value)

	return {
		metaId: found.id,
		qty: parseInt(data?.display_qty, 10) || DEFAULT_QTY,
		isActive: !!data?.enabled,
		data
	}
}

// Renders the Limited Stock button and modal for the currently opened Shopify product.
export default function LimitedStockButton(props) {
	const { updateMode, setUpdateMode, shopSelected, info, loading, modal } = props

	const [openModal, setOpenModal] = useState(false)
	const [qty, setQty] = useState(DEFAULT_QTY)
	const [metaId, setMetaId] = useState(null)
	const [isActive, setIsActive] = useState(false)
	const [loadingMeta, setLoadingMeta] = useState(false)
	const [saving, setSaving] = useState(false)

	// Push parsed metafield data into component state (button badge, modal defaults).
	const applyLimitedStockState = (found) => {
		let state = readLimitedStockFromMetafield(found)
		setMetaId(state.metaId)
		setQty(state.qty)
		setIsActive(state.isActive)
		return state
	}

	// Keep updateMode.metafields in sync after a dedicated fetch so the parent page stays current.
	const mergeLimitedStockIntoUpdateMode = (found) => {
		if(!updateMode || !found)	return

		let nextMetafields = [...(updateMode.metafields || [])]
		let pos = nextMetafields.findIndex(o => o.namespace === META_NAMESPACE && o.key === META_KEY)

		if(pos === -1)
			nextMetafields.push(found)
		else
			nextMetafields[pos] = Object.assign({}, nextMetafields[pos], found)

		setUpdateMode(Object.assign({}, updateMode, {metafields: nextMetafields}))
	}

	// On product change: show cached metafields immediately, then fetch inventory.limited_stock from Shopify.
	useEffect(() => {
		if(!updateMode?.id || !shopSelected?.id){
			applyLimitedStockState(null)
			setLoadingMeta(false)
			return
		}

		// Use metafields already loaded with the product while we fetch the dedicated key.
		let cached = findLimitedStockMetafield(updateMode.metafields)
		if(cached)
			applyLimitedStockState(cached)

		setLoadingMeta(true)
		meta.fetch({
			shop: shopSelected.id,
			urlPrefix: `products/${updateMode.id}/`,
			namespace: META_NAMESPACE,
			key: META_KEY,
		}, (r) => {
			setLoadingMeta(false)

			if(r.success && r.res?.length > 0){
				applyLimitedStockState(r.res[0])
				mergeLimitedStockIntoUpdateMode(r.res[0])
			}else if(r.success){
				applyLimitedStockState(null)
			}
		})
	}, [updateMode?.id, shopSelected?.id])

	// Re-render the shared app modal whenever modal-related state changes while it is open.
	useEffect(() => {
		if(openModal)
			showModal()
	}, [openModal, qty, metaId, saving, updateMode])

	// Insert or replace the limited_stock metafield inside updateMode after a successful save.
	const upsertMetafieldInUpdateMode = (id, metaValue) => {
		if(!updateMode)	return

		let nextMetafields = [...(updateMode.metafields || [])]
		let pos = nextMetafields.findIndex(o => o.namespace === META_NAMESPACE && o.key === META_KEY)
		let entry = {
			id,
			namespace: META_NAMESPACE,
			key: META_KEY,
			value: JSON.stringify(metaValue),
			type: "json"
		}

		if(pos === -1)
			nextMetafields.push(entry)
		else
			nextMetafields[pos] = Object.assign({}, nextMetafields[pos], entry)

		setUpdateMode(Object.assign({}, updateMode, {metafields: nextMetafields}))
	}

	// Drop limited_stock from updateMode after a successful delete.
	const removeMetafieldFromUpdateMode = () => {
		if(!updateMode)	return

		let nextMetafields = (updateMode.metafields || []).filter(o => {
			return !(o.namespace === META_NAMESPACE && o.key === META_KEY)
		})

		setUpdateMode(Object.assign({}, updateMode, {metafields: nextMetafields}))
	}

	// Hide the global modal and reset local open state.
	const closeModal = () => {
		modal({show: false})
		setOpenModal(false)
	}

	// Create or overwrite product.metafields.inventory.limited_stock with display qty and inventory baseline.
	const saveLimitedStock = () => {
		if(!updateMode?.id || !shopSelected?.id){
			info({error: "Please open a product before setting limited stock."})
			return
		}

		let displayQty = parseInt(qty, 10)
		if(!displayQty || displayQty < 1){
			info({error: "Please enter a quantity greater than 0."})
			return
		}

		let metaValue = {
			display_qty: displayQty,
			baseline_inventory: getTotalProductInventory(updateMode),
			enabled: true,
			set_at: parseInt(moment().format("X"), 10)
		}

		setSaving(true)
		loading(true)

		meta.create({
			urlPrefix: `/products/${updateMode.id}/`,
			namespace: META_NAMESPACE,
			key: META_KEY,
			shop: shopSelected.id,
			value: metaValue,
		}, (r) => {
			setSaving(false)
			loading(false)

			if(r.success){
				let savedMetaId = r.json?.id || metaId
				setMetaId(savedMetaId)
				setIsActive(true)
				upsertMetafieldInUpdateMode(savedMetaId, metaValue)
				info({success: `Limited stock set to "Only ${displayQty} left in stock - order soon".`})
				closeModal()
			}else{
				info({error: r.error || "Could not save the limited stock metafield."})
			}
		})
	}

	// Delete the limited_stock metafield from Shopify and clear local active state.
	const removeLimitedStock = () => {
		if(!metaId || !shopSelected?.id){
			info({error: "No limited stock message is currently set for this product."})
			return
		}

		setSaving(true)
		loading(true)

		meta.delete({
			shop: shopSelected.id,
			id: metaId
		}, (r) => {
			setSaving(false)
			loading(false)

			if(r.success){
				setMetaId(null)
				setIsActive(false)
				setQty(DEFAULT_QTY)
				removeMetafieldFromUpdateMode()
				info({success: "Limited stock message removed."})
				closeModal()
			}else{
				info({error: r.error || "Could not remove the limited stock metafield."})
			}
		})
	}

	// Build the Limited Stock modal (qty input, remove action, Save via modal ok footer).
	const showModal = () => {
		let found = findLimitedStockMetafield(updateMode?.metafields)
		let existingMeta = found? parseLimitedStockMeta(found.value) : null
		let remaining = found? getLimitedStockRemaining(found.value, updateMode) : null

		modal({
			key: "limited_stock_modal",
			show: true,
			title: "Limited Stock Message",
			html: () => {
				return <div className="p-3" style={{minWidth: "420px"}}>
					<div className="alert alert-info small">
						This saves <code>product.metafields.inventory.limited_stock</code> for the theme.
						The storefront can show <strong>Only X left in stock - order soon</strong> and count down automatically as Shopify inventory decreases.
					</div>
					<div className="form-group">
						<label htmlFor="limitedStockQtyInput">Starting quantity to display</label>
						<input
							id="limitedStockQtyInput"
							type="number"
							min="1"
							className="form-control"
							value={qty}
							disabled={saving}
							onChange={(e) => setQty(e.target.value)}
						/>
						<small className="form-text text-muted">
							Default is {DEFAULT_QTY}. Saving again resets the countdown from the current inventory level.
						</small>
					</div>
					{
						existingMeta?
							<div className="small text-muted mb-3">
								Current baseline inventory: {existingMeta.baseline_inventory}
								{remaining !== null? <span> | Estimated remaining now: {remaining}</span> : null}
							</div>
						: null
					}
					{
						isActive?
							<button type="button" className="btn btn-outline-danger" disabled={saving} onClick={removeLimitedStock}>
								{saving? "Removing..." : "Remove message"}
							</button>
						: null
					}
				</div>
			},
			ok: {
				title: saving? "Saving..." : "Save",
				fnc: () => {
					if(!saving)
						saveLimitedStock()
				}
			},
			return: {
				title: "Close",
				fnc: (popup, close) => {
					close()
					setOpenModal(false)
				}
			},
			exit: (popup, close) => {
				close()
				setOpenModal(false)
			}
		})
	}

	// Open the modal when the user clicks the Limited Stock button on the listing page.
	const openModalHandler = () => {
		if(!updateMode?.id){
			info({error: "Open a product first to set a limited stock message."})
			return
		}
		setOpenModal(true)
	}

	if(!updateMode)	return null

	let foundMeta = findLimitedStockMetafield(updateMode.metafields)
	let remaining = isActive?
		getLimitedStockRemaining(foundMeta?.value, updateMode)
		: null

	let buttonClass = "btn btn-lg mr-3 "
	if(loadingMeta)
		buttonClass += "btn-secondary"
	else if(isActive)
		buttonClass += "btn-success"
	else
		buttonClass += "btn-warning"

	let buttonTitle = isActive?
		`Limited stock message is active (${remaining ?? qty} left on storefront)`
		: "Set a storefront urgency message like Only X left in stock"

	return (
		<button
			type="button"
			className={buttonClass}
			disabled={loadingMeta}
			onClick={openModalHandler}
			title={buttonTitle}
		>
			Limited Stock
			{
				loadingMeta?
					<span className="badge badge-light ml-2">Loading...</span>
				: isActive?
					<span className="badge badge-light ml-2">Active · {remaining ?? qty} left</span>
				: null
			}
		</button>
	)
}
